class CartUtils {
  static subscriptionAttributesTemplate = {
    pricingVariantId: '',
    boxName: '',
    fineWinesCount: '',
    membershipPlan: '',
    allowedVariantIds: '',
    totalRedWines: '',
    totalWhiteWines: '',
    totalRoseWines: '',
  };

  // Add items to cart and conditionally remove subscription
  static async addToCart(items, options = {}) {
    try {
      const body = {
        items: Array.isArray(items) ? items : [items],
      };

      if (options.clearAttributes) {
        body.attributes = { ...this.subscriptionAttributesTemplate, ...options.attributes };
      }

      // Capture current cart
      const cartBefore = await this.fetchCart();
      const beforeKeys = cartBefore.items.map((i) => i.key);

      // : Add new item(s)
      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.message || 'Add to cart failed');
      }

      //  Fetch updated cart
      let cart = await this.fetchCart();

      const attrs = cart.attributes || {};
      let allowedIds = [];
      try {
        allowedIds = JSON.parse(attrs.allowedVariantIds || '[]').map(String);
      } catch {
        allowedIds = [];
      }

      // Identify items to KEEP
      const keepKeys = new Set(
        cart.items
          .filter((item) => item.product_type === 'membership' || allowedIds.includes(String(item.variant_id)))
          .map((item) => item.key)
      );

      // Remove any item from BEFORE that is NOT in keepKeys
      const updates = {};
      for (const item of cart.items) {
        const wasOld = beforeKeys.includes(item.key);
        const shouldKeep = keepKeys.has(item.key);
        if (wasOld && !shouldKeep) {
          updates[item.key] = 0;
        }
      }

      if (Object.keys(updates).length > 0) {
        const cleanup = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            attributes: attrs,
          }),
        });
        if (!cleanup.ok) throw new Error('Cleanup failed');
        cart = await this.fetchCart();
      }

      // Dispatch cart updated event
      this.dispatchCartEvent();

      // Update cart count bubble
      if (window.CartCountUpdater) {
        window.CartCountUpdater.updateCartIcon(cart);
      }

      return cart;
    } catch (error) {
      console.error('[CartUtils.addToCart] Fatal error:', error);
      throw error;
    }
  }

  // Helper function to dispatch cart events
  static dispatchCartEvent(eventName = 'cart:updated') {
    document.dispatchEvent(new CustomEvent(eventName));
  }

  // Update cart and conditionally remove subscription
  static async updateCart(updates, options = {}) {
    try {
      const body = {
        updates,
        attributes: {
          ...this.subscriptionAttributesTemplate,
          ...(options.attributes || {}),
        },
      };

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Failed to update cart');

      const latestCart = await this.fetchCart();

      if (this.hasSubscriptionAttributes(latestCart)) {
        await this.removeSubscriptionProducts(latestCart);
      } else {
        await this.clearSubscriptionAttributesIfNoSubsExist();
      }

      // Dispatch cart updated event
      this.dispatchCartEvent();

      // Update cart count bubble
      if (window.CartCountUpdater) {
        window.CartCountUpdater.updateCartIcon(latestCart);
      }

      return latestCart;
    } catch (error) {
      console.error('Error in updateCart:', error);
      throw error;
    }
  }

  // Checks if subscription-related cart attributes are present and non-empty
  static hasSubscriptionAttributes(cartData) {
    const subKeys = Object.keys(this.subscriptionAttributesTemplate);
    const attrs = cartData?.attributes || {};

    return subKeys.some((key) => attrs[key]);
  }

  // Removes subscription items and clears attributes
  static async removeSubscriptionProducts(cartData) {
    try {
      const attrs = cartData.attributes || {};
      const pricingVariantId = attrs.pricingVariantId;

      let allowedIdsArray = [];

      try {
        let parsed,
          allowedVariantIds = attrs.allowedVariantIds;

        if (typeof allowedVariantIds === 'string') {
          // If it's a string, parse it back to an array
          parsed = allowedVariantIds.split(',').map((id) => id.trim());
        } else if (Array.isArray(allowedVariantIds)) {
          // If it's already an array
          parsed = allowedVariantIds;
        } else {
          // Handle other cases (null, undefined, etc.)
          parsed = [];
        }

        if (Array.isArray(parsed)) {
          allowedIdsArray = parsed.map((id) => String(id).trim());
        } else {
          console.warn('allowedVariantIds not an array:', parsed);
        }
      } catch (e) {
        console.warn('allowedVariantIds could not be parsed:', attrs.allowedVariantIds);
      }

      // Count occurrences of each variant ID in allowedVariantIds
      const allowedIdCounts = {};
      allowedIdsArray.forEach((id) => {
        allowedIdCounts[id] = (allowedIdCounts[id] || 0) + 1;
      });

      const updates = {};

      if (pricingVariantId) {
        updates[pricingVariantId] = 0;
      }

      (cartData.items || []).forEach((item) => {
        const isMembership = item.product_type === 'membership';
        const itemVariantId = String(item.variant_id);
        const isAllowedVariant = allowedIdCounts.hasOwnProperty(itemVariantId);

        if (isMembership) {
          // For membership items, remove all quantities
          updates[item.key] = 0;
        } else if (isAllowedVariant) {
          // For allowed variants, remove only the quantity that matches the count in allowedVariantIds
          const countToRemove = allowedIdCounts[itemVariantId];
          const currentQuantity = item.quantity || 0;
          const newQuantity = Math.max(0, currentQuantity - countToRemove);

          if (newQuantity === 0) {
            updates[item.key] = 0; // Remove completely
          } else {
            updates[item.key] = newQuantity; // Update to remaining quantity
          }
        }
      });

      if (Object.keys(updates).length === 0) {
        console.warn('No subscription items matched for removal');
        return;
      }

      const body = {
        updates,
        attributes: { ...this.subscriptionAttributesTemplate },
      };

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Failed to remove subscription products');
      await res.json();
    } catch (error) {
      console.error('Error in removeSubscriptionProducts:', error);
    }
  }

  static async removeSubscriptionFamily(cartData, allowedVariantIds = []) {
    const attrs = cartData.attributes || {};
    const pricingVariantId = attrs.pricingVariantId;

    const allAllowedIds = allowedVariantIds.length
      ? allowedVariantIds
      : (() => {
          try {
            return JSON.parse(attrs.allowedVariantIds || '[]').map(String);
          } catch {
            return [];
          }
        })();

    // Count occurrences of each variant ID in allowedVariantIds
    const allowedIdCounts = {};
    allAllowedIds.forEach((id) => {
      allowedIdCounts[id] = (allowedIdCounts[id] || 0) + 1;
    });

    const updates = {};

    if (pricingVariantId) updates[pricingVariantId] = 0;

    (cartData.items || []).forEach((item) => {
      const isMembership = item.product_type === 'membership';
      const itemVariantId = String(item.variant_id);
      const isAllowed = allowedIdCounts.hasOwnProperty(itemVariantId);

      if (isMembership) {
        // For membership items, remove all quantities
        updates[item.key] = 0;
      } else if (isAllowed) {
        // For allowed variants, remove only the quantity that matches the count in allowedVariantIds
        const countToRemove = allowedIdCounts[itemVariantId];
        const currentQuantity = item.quantity || 0;
        const newQuantity = Math.max(0, currentQuantity - countToRemove);

        if (newQuantity === 0) {
          updates[item.key] = 0; // Remove completely
        } else {
          updates[item.key] = newQuantity; // Update to remaining quantity
        }
      }
    });

    if (Object.keys(updates).length === 0) return;

    const body = {
      updates,
      attributes: { ...this.subscriptionAttributesTemplate },
    };

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error('Failed to remove subscription family');
  }

  //  Clears attributes if no membership products remain in cart
  static async clearSubscriptionAttributesIfNoSubsExist() {
    try {
      const cart = await this.fetchCart();
      const hasMembership = cart.items?.some((item) => item.product_type === 'membership');

      if (!hasMembership) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { ...this.subscriptionAttributesTemplate } }),
        });
      }
    } catch (error) {
      console.error('Error in clearSubscriptionAttributesIfNoSubsExist:', error);
    }
  }

  //Helper: Fetch current cart data
  static async fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }
}

// Expose globally
window.CartUtils = CartUtils;
